import React, { useMemo } from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import { Box, xcss } from '@atlaskit/primitives';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { ChildIssuesLimitStatus } from '@atlassian/jira-issue-view-common-constants/src/child-issues.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useIsAiEnabledForIssue } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { IssueBreakdownButton } from '@atlassian/jira-ai-work-breakdown/src/ui/issue-breakdown-button/index.tsx';
import { useIsAiEnabled } from '../utils.tsx';
import type { AddChildEventHandler } from './add-child/types.tsx';
import AddChild from './add-child/index.tsx';
import Meatballs from './meatballs/src/index.tsx';
import SortByMenu from './sort-by-menu/index.tsx';

type OldSectionHeadingIconsProps = {
	isSortingAvailable: boolean;
	childIssuesLimitStatus: ChildIssuesLimitStatus;
	isMeatballMenuEnabled: boolean;
	supportsIssueCreation: boolean | undefined;
	onAddChildClick: AddChildEventHandler | undefined;
	issueHierarchyLevel?: number;
	containerWidth?: number;
};

// This number was calculated by hand to see when buttons started to overflow
const SINGLE_COLUMN_MAX_WIDTH = 440;

export const OldSectionHeadingIcons = ({
	isSortingAvailable,
	childIssuesLimitStatus,
	isMeatballMenuEnabled,
	supportsIssueCreation,
	onAddChildClick,
	issueHierarchyLevel,
	containerWidth,
}: OldSectionHeadingIconsProps) => {
	const isAiEnabled = useIsAiEnabled();
	const isAnonymousUser = useIsAnonymous();
	const isAiOptInEnabled = useIsAiEnabledForIssue();

	const renderIssueBreakdownButton = useMemo(() => {
		return (
			<SpotlightManager>
				<SpotlightTarget name="suggestIssuesSpotlight">
					<IssueBreakdownButton
						issueHierarchyLevel={issueHierarchyLevel}
						isCompact={
							(!!containerWidth && containerWidth <= SINGLE_COLUMN_MAX_WIDTH) ||
							expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false)
						}
					/>
				</SpotlightTarget>
				<EngagementSpotlight engagementId="suggestIssuesSpotlight" />
			</SpotlightManager>
		);
	}, [issueHierarchyLevel, containerWidth]);

	return useMemo(() => {
		const elements: React.ReactElement[] = [
			// Add SortByMenu if sorting is available
			...(isSortingAvailable
				? [<SortByMenu key="sort-by" childIssuesLimitStatus={childIssuesLimitStatus} />]
				: []),

			// Add Meatballs if menu is enabled
			...(isMeatballMenuEnabled
				? [<Meatballs key="meatballs" childIssuesLimitStatus={childIssuesLimitStatus} />]
				: []),

			// Add AddChild if issue creation is supported
			...(supportsIssueCreation ? [<AddChild key="add-child" onClick={onAddChildClick} />] : []),

			// Add IssueBreakdownButton if conditions are met
			...(!isAnonymousUser &&
			supportsIssueCreation &&
			isAiEnabled &&
			isAiOptInEnabled &&
			!expVal('proactive_aiwb_2', 'enabled', false)
				? [
						<Box key="issue-breakdown" xcss={oldIssueBreakdownButtonStyles}>
							{renderIssueBreakdownButton}
						</Box>,
					]
				: []),
		];

		return elements;
	}, [
		isSortingAvailable,
		childIssuesLimitStatus,
		isMeatballMenuEnabled,
		supportsIssueCreation,
		onAddChildClick,
		isAnonymousUser,
		isAiEnabled,
		isAiOptInEnabled,
		renderIssueBreakdownButton,
	]);
};

const oldIssueBreakdownButtonStyles = xcss({
	marginLeft: 'space.075',
});
